import { useState, useCallback, createContext, useContext } from 'react';

/**
 * Toast notification context
 */
const ToastContext = createContext(null);

/**
 * Toast types
 */
export const TOAST_TYPES = {
  SUCCESS: 'success',
  ERROR: 'error',
  WARNING: 'warning',
  INFO: 'info',
};

/**
 * Toast Provider Component
 * Wrap your app with this provider to enable toast notifications
 * 
 * @example
 * <ToastProvider>
 *   <App />
 * </ToastProvider>
 */
export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const showToast = useCallback((message, type = TOAST_TYPES.INFO, duration = 5000) => {
    const id = Date.now() + Math.random();
    const toast = { id, message, type, duration };

    setToasts(prev => [...prev, toast]);

    // Auto-dismiss after duration
    if (duration > 0) {
      setTimeout(() => {
        removeToast(id);
      }, duration);
    }

    return id;
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const clearAll = useCallback(() => {
    setToasts([]);
  }, []);

  const value = {
    toasts,
    showToast,
    removeToast,
    clearAll,
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastContainer />
    </ToastContext.Provider>
  );
};

/**
 * Hook to use toast notifications
 * 
 * @example
 * const { showToast } = useToast();
 * showToast('Operation successful!', 'success');
 */
export const useToast = () => {
  const context = useContext(ToastContext);
  
  if (!context) {
    // Fallback if provider is not set up
    return {
      toasts: [],
      showToast: (message, type) => {
        console.warn('[useToast] ToastProvider not found. Message:', message);
        // Fallback to console
        const prefix = type === TOAST_TYPES.ERROR ? '❌' : 
                      type === TOAST_TYPES.SUCCESS ? '✅' : 
                      type === TOAST_TYPES.WARNING ? '⚠️' : 'ℹ️';
        console.log(`${prefix} ${message}`);
      },
      removeToast: () => {},
      clearAll: () => {},
    };
  }
  
  return context;
};

/**
 * Toast Container Component
 * Renders all active toasts
 */
const ToastContainer = () => {
  const { toasts, removeToast } = useToast();

  if (toasts.length === 0) return null;

  return (
    <div className="fixed top-4 right-4 z-50 space-y-2 max-w-md">
      {toasts.map(toast => (
        <Toast
          key={toast.id}
          toast={toast}
          onClose={() => removeToast(toast.id)}
        />
      ))}
    </div>
  );
};

/**
 * Individual Toast Component
 */
const Toast = ({ toast, onClose }) => {
  const { message, type } = toast;

  const bgColor = {
    success: 'bg-green-500',
    error: 'bg-red-500',
    warning: 'bg-yellow-500',
    info: 'bg-blue-500',
  }[type] || 'bg-gray-500';

  const icon = {
    success: '✓',
    error: '✕',
    warning: '⚠',
    info: 'ℹ',
  }[type] || 'ℹ';

  return (
    <div
      className={`${bgColor} text-white px-6 py-4 rounded-lg shadow-lg flex items-start gap-3 animate-slideIn`}
      role="alert"
    >
      <span className="text-xl font-bold">{icon}</span>
      <p className="flex-1 text-sm">{message}</p>
      <button
        onClick={onClose}
        className="text-white hover:text-gray-200 transition-colors ml-2"
        aria-label="Close notification"
      >
        <svg
          className="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>
    </div>
  );
};

/**
 * Helper functions for common toast types
 */
export const toast = {
  success: (message, duration) => {
    // This is a placeholder - actual implementation will use the hook
    console.log('✅', message);
  },
  error: (message, duration) => {
    console.error('❌', message);
  },
  warning: (message, duration) => {
    console.warn('⚠️', message);
  },
  info: (message, duration) => {
    console.info('ℹ️', message);
  },
};

export default useToast;
